import React, { useState } from "react";
import { Loader2, Mail } from "lucide-react";
import { postTermsAgreements } from "@/apis/user/route";
import { TermsAgreementType } from "@/apis/user/types";
import { ContactButton } from "@/components/common/contact-button";
import { LoginLayout, LoginLeftPanel } from "@/components/login";
import { PolicyRow } from "@/components/login/policy-row";
import { TERMS_AGREEMENT_ITEMS } from "@/constants/terms";
import { useShake } from "@/components/login/use-shake";
import { auth } from "@/lib/firebase";
import { cn } from "@/lib/utils";
import { signOutUser } from "@/services/auth-service";

interface TermsAgreementPageProps {
  pendingTerms: TermsAgreementType[];
  onCompleted: () => void;
}

export const TermsAgreementPage: React.FC<TermsAgreementPageProps> = ({
  pendingTerms,
  onCompleted,
}) => {
  const [checked, setChecked] = useState<Set<TermsAgreementType>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isShaking, triggerShake } = useShake();
  // 동의 실패는 다시 시도할 지점(제출 버튼) 옆에 남아 있어야 하므로 사라지는 toast 대신 인라인으로 노출한다.
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const currentUser = auth.currentUser;
  const userEmail = currentUser?.email ?? "";
  const userLabel = currentUser?.displayName
    ? `${currentUser.displayName}(${userEmail})`
    : userEmail;

  const allChecked = pendingTerms.every((terms) => checked.has(terms));

  const toggle = (terms: TermsAgreementType) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(terms)) {
        next.delete(terms);
      } else {
        next.add(terms);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!allChecked) {
      triggerShake();
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      await postTermsAgreements([...checked]);
      onCompleted();
    } catch {
      setErrorMessage("약관 동의 처리에 실패했습니다. 다시 시도해 주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LoginLayout>
      <LoginLeftPanel>
        <div className="flex flex-col gap-6 items-center w-full max-w-[374px] rounded-[10px]">
          {/* Header */}
          <div className="flex flex-col items-center gap-4 w-full text-center">
            <h1 className="font-['Pretendard_Variable'] font-bold text-[24px] text-primary tracking-[-0.552px] w-full">
              약관 동의
            </h1>
            <p className="text-[14px] leading-5 text-label-alternative">
              서비스 이용을 위해
              <br />
              약관 동의가 필요합니다.
            </p>
          </div>

          {/* User info */}
          {userEmail && (
            <div className="flex w-full items-center justify-center gap-2 px-4 py-3 rounded-lg bg-background-gray-alternative">
              <Mail className="w-[18px] h-[18px] shrink-0 text-blue-500" />
              <span className="text-[12px] font-medium text-primary truncate">
                {userLabel}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-10 w-full">
            {/* Terms agreement */}
            <div className="flex flex-col gap-1 w-full">
              {pendingTerms.map((terms) => (
                <PolicyRow
                  key={terms}
                  label={TERMS_AGREEMENT_ITEMS[terms].label}
                  checked={checked.has(terms)}
                  onToggle={() => toggle(terms)}
                  viewUrl={TERMS_AGREEMENT_ITEMS[terms].url}
                  shake={isShaking && !checked.has(terms)}
                />
              ))}
            </div>

            {/* Submit Button */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={cn(
                "w-full h-10 px-3 rounded-[6px] bg-primary-base text-static-white text-[15px] font-medium flex items-center justify-center",
                (!allChecked || isSubmitting) && "opacity-24",
              )}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-static-white" />
                  <span>처리 중...</span>
                </div>
              ) : (
                "동의하고 시작하기"
              )}
            </button>

            {errorMessage && (
              <p className="text-[12px] text-status-error text-center w-full -mt-6">
                {errorMessage}
              </p>
            )}

            {/* Footer */}
            <div className="flex flex-col items-center gap-3 w-full">
              <button
                type="button"
                onClick={() => signOutUser()}
                className="text-[14px] font-medium text-label-alternative underline"
              >
                로그아웃
              </button>
              <div className="flex items-center justify-center gap-3">
                <span className="text-[14px] font-medium text-label-normal">
                  문제가 생겼나요?
                </span>
                <ContactButton className="!text-label-normal" />
              </div>
            </div>
          </div>
        </div>
      </LoginLeftPanel>
    </LoginLayout>
  );
};
